/**
 * Shop Settings Hooks
 *
 * TanStack Query hooks for shop configuration and settings management
 * Optimized for caching with 10-minute refresh intervals
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    deleteDisabledSlotApi,
    getAppointmentsBySlotApi,
    getAvailableDatesApi,
    getCustomerNotificationTemplatesApi,
    getDisabledSlotsApi,
    getDiscountProgramsApi,
    getEmailThankYouDetailsApi,
    getSequenceNumberConfigApi,
    getShopConfigApi,
    getShopTaxesApi,
    getShopProfileDetailApi,
    getSlotsWithDisabledApi,
    getTermsAndWarrantyApi,
    getvisibilitySettingApi,
    sendTestThankYouEmailApi,
    updateBufferConfigApi,
    updateCustomerNotificationTemplatesApi,
    updateDisabledSlotsApi,
    updateDropOffSlotsConfigApi,
    updateEmailThankYouDetailsApi,
    updateShopTaxesApi,
    updateSequenceNumberConfigApi,
    updateShopConfigApi,
    updateShopProfileDetailApi,
    updatevisibilitySettingApi,
} from './settings.api';
import { showTaxSettingsNotice } from '../../../common/TaxSettingsNotice';

// Query Keys for core settings domain
export const coreSettingsKeys = {
    all: ['settings'],
    config: () => [...coreSettingsKeys.all, 'shop-config'],
    pdfSetting: () => [...coreSettingsKeys.all, 'visibility-list'],
    shopProfileDetail: () => [...coreSettingsKeys.all, 'shop-profile-detail'],
    termsAndWarranty: () => [...coreSettingsKeys.all, 'terms-and-warranty'],
    emailThankYou: () => [...coreSettingsKeys.all, 'email-thank-you'],
    sequenceConfig: () => [...coreSettingsKeys.all, 'sequence-config'],
    discountProgram: (params) => [...coreSettingsKeys.all, 'discount-program', params],
    customerNotificationTemplates: () => [...coreSettingsKeys.all, 'customer-notification-templates'],
    slotsWithDisabled: {
        all: () => [...coreSettingsKeys.all, 'slots-with-disabled'],
        byShopAndDate: (shopId, date, includeAppointmentCount) => [...coreSettingsKeys.all, 'slots-with-disabled', shopId, date, includeAppointmentCount],
    },
    availableDates: {
        all: () => [...coreSettingsKeys.all, 'available-dates'],
        byShopMonthYear: (shopId, year, month) => [...coreSettingsKeys.all, 'available-dates', shopId, year, month],
    },
    appointmentsBySlot: {
        all: () => [...coreSettingsKeys.all, 'appointments-by-slot'],
        byShopDateSlot: (shopId, date, slotId) => [...coreSettingsKeys.all, 'appointments-by-slot', shopId, date, slotId],
    },
    disabledSlotsList: () => [...coreSettingsKeys.all, 'disabled-slots-list'],
    shopTaxes: () => [...coreSettingsKeys.all, 'shop-taxes'],
};

/**
 * Hook to get shop configuration
 *
 * Uses optimized default settings from queryClient:
 * - 10-minute cache (no repeated API calls)
 * - Auto refresh every 10 minutes
 * - Smart retry logic
 * - Performance optimized refetch behavior
 *
 * @param {object} options - Query options
 * @returns {object} Query result with shop config data
 */
export const useGetShopConfig = (options = {}) => {
    return useQuery({
        queryKey: coreSettingsKeys.config(),
        queryFn: getShopConfigApi,
        ...options,
    });
};

/**
 * Hook to get pdf settings structure visibility
 *
 * Uses optimized default settings from queryClient:
 * - 10-minute cache (no repeated API calls)
 * - Auto refresh every 10 minutes
 * - Smart retry logic
 * - Performance optimized refetch behavior
 *
 * @param {object} options - Query options
 * @returns {object} Query result with shop config data
 */
export const useGetvisibilitySetting = (options = {}) => {
    return useQuery({
        queryKey: coreSettingsKeys.pdfSetting(),
        queryFn: getvisibilitySettingApi,
        ...options,
    });
};

// update pdf setting visibility
export const useUpdatevisibilitySetting = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updatevisibilitySettingApi,
        onSuccess: (data) => {
            queryClient.refetchQueries({ queryKey: coreSettingsKeys.pdfSetting() });
            toast.success(data?.message || 'Changes Saved.');
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to update visibility settings');
        },
    });
};

/**
 * Hook to get terms and warranty data
 *
 * Uses optimized default settings from queryClient:
 * - 10-minute stale time for efficient caching
 * - Automatic background refetching when data becomes stale
 * - 5-minute cache time for performance
 * - Automatic retry on failure
 *
 * @param {Object} options - Additional query options
 * @returns {Object} Query result with data, loading, error states
 */
export const useGetTermsAndWarranty = (options = {}) => {
    return useQuery({
        queryKey: coreSettingsKeys.termsAndWarranty(),
        queryFn: getTermsAndWarrantyApi,
        ...options,
    });
};

/**
 * Hook to get Shop personal data
 *
 * Uses optimized default settings from queryClient:
 * - 10-minute stale time for efficient caching
 * - Automatic background refetching when data becomes stale
 * - 5-minute cache time for performance
 * - Automatic retry on failure
 *
 * @param {Object} options - Additional query options
 * @returns {Object} Query result with data, loading, error states
 */
export const useGetShopProfileDetail = (options = {}) => {
    return useQuery({
        queryKey: coreSettingsKeys.shopProfileDetail(),
        queryFn: getShopProfileDetailApi,
        ...options,
    });
};

/**
 * Hook to get shop module sequence configuration
 *
 * Uses optimized default settings from queryClient:
 * - 10-minute stale time for efficient caching
 * - Automatic background refetching when data becomes stale
 *
 * @param {Object} options - Additional query options
 * @returns {Object} Query result with sequence config data
 */
export const useGetSequenceNumberConfig = (options = {}) => {
    return useQuery({
        queryKey: coreSettingsKeys.sequenceConfig(),
        queryFn: getSequenceNumberConfigApi,
        ...options,
    });
};

/**
 * Hook to update shop module sequence configuration
 *
 * Invalidates sequence config after update to keep UI fresh
 *
 * @returns {Object} Mutation object with loading, error, and success states
 */
export const useUpdateSequenceNumberConfig = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateSequenceNumberConfigApi,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: coreSettingsKeys.sequenceConfig() });
        },
    });
};

/**
 * Hook to get discount programs list
 *
 * Uses optimized default settings from queryClient:
 * - 10-minute stale time for efficient caching
 * - Automatic background refetching when data becomes stale
 * - 5-minute cache time for performance
 * - Automatic retry on failure
 *
 * @param {object} params - Query parameters (e.g., { pagination: false })
 * @param {object} options - Query options
 * @returns {object} Query result with discount programs data
 */
export const useGetDiscountPrograms = (params = { pagination: false }, options = {}) => {
    return useQuery({
        queryKey: coreSettingsKeys.discountProgram(params),
        queryFn: () => getDiscountProgramsApi(params),
        ...options,
    });
};

/**
 * Hook to update shop detail
 *
 * @returns {Object} Mutation object with loading, error, and success states
 */
export const useUpdateShopProfileDetail = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateShopProfileDetailApi,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: coreSettingsKeys.shopProfileDetail() });
        },
    });
};

/**
 * Hook to get email thank you details
 *
 * @param {Object} options - Additional query options
 * @returns {Object} Query result with email thank you data
 */
export const useGetEmailThankYouDetails = (options = {}) => {
    return useQuery({
        queryKey: coreSettingsKeys.emailThankYou(),
        queryFn: getEmailThankYouDetailsApi,
        ...options,
    });
};

/**
 * Hook to update buffer configuration
 *
 * @returns {Object} Mutation object with loading, error, and success states
 */
export const useUpdateBufferConfig = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateBufferConfigApi,
        onSuccess: () => {
            // Invalidate and refetch shop config after successful update
            queryClient.invalidateQueries({ queryKey: coreSettingsKeys.config() });
        },
    });
};

/**
 * Hook to update shop configuration
 *
 * @returns {Object} Mutation object with loading, error, and success states
 */
export const useUpdateShopConfig = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateShopConfigApi,
        onSuccess: () => {
            // Invalidate and refetch shop config and notification templates after successful update
            queryClient.invalidateQueries({ queryKey: coreSettingsKeys.config() });
            queryClient.invalidateQueries({ queryKey: coreSettingsKeys.customerNotificationTemplates() });
        },
    });
};

/**
 * Hook to update email thank you details
 *
 * @returns {Object} Mutation object with loading, error, and success states
 */
export const useUpdateEmailThankYouDetails = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateEmailThankYouDetailsApi,
        onSuccess: () => {
            // Invalidate and refetch email thank you details after successful update
            queryClient.invalidateQueries({ queryKey: coreSettingsKeys.emailThankYou() });
        },
    });
};

/**
 * Hook to send test thank you email
 *
 * @returns {Object} Mutation object with loading, error, and success states
 */
export const useSendTestThankYouEmail = () => {
    return useMutation({
        mutationFn: sendTestThankYouEmailApi,
    });
};

/**
 * Hook for updating drop-off slots configuration
 * Updates the drop-off slots settings and invalidates shop config cache
 *
 * @returns {Object} Mutation object with loading, error, and success states
 */
export const useUpdateDropOffSlotsConfig = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateDropOffSlotsConfigApi,
        onSuccess: (data) => {
            // Invalidate shop config to refresh data
            queryClient.invalidateQueries({ queryKey: coreSettingsKeys.config() });
            // Also invalidate the disabled slots list
            queryClient.invalidateQueries({
                queryKey: coreSettingsKeys.slotsWithDisabled.all(),
                exact: false, // Invalidate all queries that start with this key
                refetchType: 'all',
            });
            // Also invalidate the disabled slots list
            queryClient.invalidateQueries({
                queryKey: coreSettingsKeys.disabledSlotsList(),
            });
            // Invalidate available dates queries since disabling slots might affect date availability
            queryClient.invalidateQueries({
                queryKey: coreSettingsKeys.availableDates.all(),
                exact: false,
                refetchType: 'all',
            });
            toast.success(data.message || 'Changes Saved');
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to update drop-off slots');
        },
    });
};

/**
 * Hook to get shop taxes
 *
 * @param {object} options - Query options
 * @returns {object} Query result with shop taxes data
 */
export const useGetShopTaxes = (options = {}) => {
    return useQuery({
        queryKey: coreSettingsKeys.shopTaxes(),
        queryFn: getShopTaxesApi,
        ...options,
    });
};

/**
 * Hook to update shop taxes
 *
 * @returns {Object} Mutation object with loading, error, and success states
 */
export const useUpdateShopTaxes = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateShopTaxesApi,
        onSuccess: () => {
            showTaxSettingsNotice(
                'Updated tax settings will apply to all new invoices and estimates, as well as any existing finalized invoices or estimates if you edit them. ',
                5000
            );

            queryClient.invalidateQueries({
                queryKey: coreSettingsKeys.shopTaxes(),
            });

            queryClient.invalidateQueries({
                queryKey: coreSettingsKeys.config(),
            });
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to update shop taxes');
        },
    });
};

/**
 * Hook to get available slots with disabled status for a specific date
 * Uses query pattern with POST request for caching and automatic refetching
 *
 * @param {Object} params - Query parameters
 * @param {string} params.shopId - Shop ID
 * @param {string} params.date - Date in YYYY-MM-DD format
 * @param {Object} options - Additional query options
 * @returns {Object} Query object with data, isLoading, isError, etc.
 */
export const useGetSlotsWithDisabled = ({ shopId, date, includeAppointmentCount = false }, options = {}) => {
    return useQuery({
        queryKey: coreSettingsKeys.slotsWithDisabled.byShopAndDate(shopId, date, includeAppointmentCount),
        queryFn: () => getSlotsWithDisabledApi({ shopId, date, includeAppointmentCount }),
        enabled: !!shopId && !!date, // Only run query when shopId and date are available
        staleTime: 0, // Always fetch fresh data when modal opens or date changes
        gcTime: 0, // Keep in cache for 5 minutes
        ...options,
    });
};

/**
 * Hook to update disabled slots for a specific date
 * Uses mutation pattern to update the disabled status of time slots
 *
 * @returns {Object} Mutation object with loading, error, and success states
 */
export const useUpdateDisabledSlots = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateDisabledSlotsApi,
        onSuccess: (data, variables) => {
            toast.success('Disabled slots updated successfully');

            // Invalidate the cache for slots queries after update
            // This will trigger a refetch when the component requests this data again
            if (variables?.date) {
                queryClient.invalidateQueries({
                    queryKey: coreSettingsKeys.slotsWithDisabled.all(),
                    exact: false, // Invalidate all queries that start with this key
                    refetchType: 'all',
                });
                // Also invalidate the disabled slots list
                queryClient.invalidateQueries({
                    queryKey: coreSettingsKeys.disabledSlotsList(),
                });
                // Invalidate available dates queries since disabling slots might affect date availability
                queryClient.invalidateQueries({
                    queryKey: coreSettingsKeys.availableDates.all(),
                    exact: false,
                    refetchType: 'all',
                });
            }
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to update disabled slots');
        },
    });
};

/**
 * Hook to get all disabled slots list
 * Uses query pattern for caching and automatic refetching
 *
 * @param {Object} options - Additional query options
 * @returns {Object} Query object with data, isLoading, isError, etc.
 */
export const useGetDisabledSlots = (options = {}) => {
    return useQuery({
        queryKey: coreSettingsKeys.disabledSlotsList(),
        queryFn: getDisabledSlotsApi,
        staleTime: 0, // Always fetch fresh data when page loads or modal opens
        gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
        ...options,
    });
};

/**
 * Hook to delete a disabled slot
 * Uses mutation pattern to delete a specific disabled slot
 * Automatically invalidates related queries after deletion
 *
 * @returns {Object} Mutation object with loading, error, and success states
 */
export const useDeleteDisabledSlot = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteDisabledSlotApi,
        onSuccess: () => {
            toast.success('Disabled slot deleted successfully');

            queryClient.invalidateQueries({
                queryKey: coreSettingsKeys.slotsWithDisabled.all(),
                exact: false, // Invalidate all queries that start with this key
                refetchType: 'all',
            });
            // Also invalidate the disabled slots list
            queryClient.invalidateQueries({
                queryKey: coreSettingsKeys.disabledSlotsList(),
            });
            // Invalidate available dates queries since disabling slots might affect date availability
            queryClient.invalidateQueries({
                queryKey: coreSettingsKeys.availableDates.all(),
                exact: false,
                refetchType: 'all',
            });
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to delete disabled slot');
        },
    });
};

/**
 * Hook to get available dates for a specific month and year
 * Uses query pattern with POST request for caching and automatic refetching
 * Automatically refetches when month/year changes or when disabled slots are updated
 *
 * @param {Object} params - Query parameters
 * @param {string} params.shopId - Shop ID
 * @param {number} params.year - Year (e.g., 2025)
 * @param {number} params.month - Month (1-12)
 * @param {Object} options - Additional query options
 * @returns {Object} Query object with data, isLoading, isError, etc.
 */
export const useGetAvailableDates = ({ shopId, year, month, includeAppointmentCount }, options = {}) => {
    return useQuery({
        queryKey: coreSettingsKeys.availableDates.byShopMonthYear(shopId, year, month, includeAppointmentCount),
        queryFn: () => getAvailableDatesApi({ shopId, year, month, includeAppointmentCount }),
        enabled: !!shopId && !!year && !!month, // Only run query when all params are available
        staleTime: 0, // Always fetch fresh data when modal opens or month changes
        gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
        ...options,
    });
};

/**
 * Hook to get appointments by slot
 * Uses query pattern with POST request for caching and automatic refetching
 *
 * @param {Object} params - Query parameters
 * @param {string} params.shopId - Shop ID
 * @param {string} params.date - Date in YYYY-MM-DD format
 * @param {string} params.slotId - Slot ID
 * @param {Object} options - Additional query options
 * @returns {Object} Query object with data, isLoading, isError, etc.
 */
export const useGetAppointmentsBySlot = ({ shopId, date, slotId }, options = {}) => {
    return useQuery({
        queryKey: coreSettingsKeys.appointmentsBySlot.byShopDateSlot(shopId, date, slotId),
        queryFn: () => getAppointmentsBySlotApi({ shopId, date, slotId }),
        enabled: !!shopId && !!date && !!slotId, // Only run query when all params are available
        staleTime: 0, // Always fetch fresh data
        gcTime: 0, // Don't cache
        ...options,
    });
};

/**
 * Hook to fetch customer notification templates
 * Route: web/v4/api/customer-notification-templates (GET)
 * @param {Object} options - Additional query options
 * @returns {Object} Query result object
 */
export const useGetCustomerNotificationTemplates = (options = {}) => {
    return useQuery({
        queryKey: coreSettingsKeys.customerNotificationTemplates(),
        queryFn: getCustomerNotificationTemplatesApi,
        ...options,
    });
};

/**
 * Hook to update customer notification templates
 * Route: web/v4/api/customer-notification-templates (PUT)
 * @returns {Object} Mutation result object
 */
export const useUpdateCustomerNotificationTemplates = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateCustomerNotificationTemplatesApi,
        onSuccess: () => {
            // Invalidate and refetch customer notification templates
            queryClient.invalidateQueries({ queryKey: coreSettingsKeys.customerNotificationTemplates() });
        },
    });
};

/**
 * Hook to fetch the isEnabled status for 'appointmentConfirmed' customer notification template
 * @param {Object} options - Additional query options
 * @returns {Object} { isEnabled, isAppointmentConfirmedDisabled, appointmentConfirmedItem, isLoading, isSuccess, error }
 */
export const useGetAppointmentConfirmedNotificationStatus = (options = {}) => {
    const queryResult = useGetCustomerNotificationTemplates(options);
    const { data } = queryResult;

    const appointmentList = data?.templates?.appointment_texts?.appointment || [];
    const appointmentConfirmedItem = appointmentList.find((item) => item.type === 'appointmentConfirmed');

    // isEnabled defaults to true if not specified; isAppointmentConfirmedDisabled is opposite
    const isEnabled = appointmentConfirmedItem?.isEnabled !== undefined ? Boolean(appointmentConfirmedItem.isEnabled) : true;
    const isAppointmentConfirmedDisabled = !isEnabled;

    return {
        ...queryResult,
        isEnabled,
        isAppointmentConfirmedDisabled,
        appointmentConfirmedItem,
    };
};
